import type {AnalyticsEvent} from '@github-ui/use-analytics'
import {useAnalytics} from '@github-ui/use-analytics'
import {useCallback} from 'react'

import type {ActivityClickTarget} from '../types/analytics-event-types'

type SendActivityEventFunction = (
  target: ActivityClickTarget,
  payload?: {[key: string]: unknown} | AnalyticsEvent,
) => void

/**
 * Use this hook to send user analytics events to the data warehouse.
 * Please note that this hook uses `sendEvent` helper of the Hydro analytics client under the hood,
 * to enrich event context with additional information about the user, repository, and current page.
 * See: https://thehub.github.com/epd/engineering/products-and-services/internal/hydro/installation/browser-events/
 *
 * You can find a list of all included context properties in `app/helpers/octolytics_helper.rb`.
 * The majority of the events don't require additional context properties as they are sent by default,
 * so check the existing properties in `octolytics_helper` before adding new ones.
 *
 * @example
 * ```tsx
 * function Component() {
 *   const { sendActivityClickEvent } = useActivityAnalytics()
 *   return <ActionMenu onOpenChange={open => open && sendActivityClickEvent('OVERFLOW_MENU')}>...</ActionMenu>
 * }
 * ```
 */
export function useActivityAnalytics(): {
  sendActivityClickEvent: SendActivityEventFunction
} {
  const {sendAnalyticsEvent} = useAnalytics()

  return {
    sendActivityClickEvent: useCallback(
      (target, payload = {}) => {
        sendAnalyticsEvent('repos-activity.click', target, payload)
      },
      [sendAnalyticsEvent],
    ),
  }
}
